import type { SessionUser } from '@flux/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { clearCsrfToken } from '@/lib/api/csrf.js';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { fetchCurrentUser, logout as logoutRequest } from './api.js';

/**
 * Session courante — alimente le garde d'authentification (`beforeLoad` de
 * `_authenticated`) et le menu de compte (`TopBar`). `staleTime` généreux :
 * `GET /auth/me` n'a pas besoin d'être revalidé à chaque focus de fenêtre, la
 * garde de route s'en charge déjà à chaque navigation protégée.
 */
export function useCurrentUser(): UseQueryResult<SessionUser> {
  return useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: fetchCurrentUser,
    staleTime: 60_000,
    retry: false,
  });
}

export function useLogout(): { logout: () => void; isPending: boolean } {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      clearCsrfToken();
      queryClient.removeQueries({ queryKey: queryKeys.auth.me() });
      void navigate({ to: '/connexion' });
    },
    onError: () => {
      toast.error('La déconnexion a échoué côté serveur ; la session locale a été effacée.');
    },
  });

  return { logout: () => mutation.mutate(), isPending: mutation.isPending };
}
